import { API_BASE } from '@/shared/constants';
import { getBrowserCSRF, invalidateBrowserSession } from '@/modules/browser-auth/api/browser-session';
import { getTelegramInitData, readDevTelegramIdFromQuery } from '@/shared/telegram';

/** Apply the current browser session to every game API, just like initData headers. */
export function buildApiUrl(path: string): string {
  const url = `${API_BASE}${path}`;
  if (getTelegramInitData()) return url;
  const telegramId = readDevTelegramIdFromQuery();
  if (telegramId == null) return url;
  const queryIndex = path.indexOf('?');
  const query = new URLSearchParams(queryIndex < 0 ? '' : path.slice(queryIndex + 1));
  if (query.has('telegramId')) return url;
  return `${url}${queryIndex < 0 ? '?' : '&'}telegramId=${telegramId}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: string,
  ) {
    super(`API Error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

export function buildHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extra,
  };

  const initData = getTelegramInitData();
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  } else {
    const csrf = getBrowserCSRF();
    if (csrf) headers['X-Game-CSRF'] = csrf;
  }

  return headers;
}

export async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 401 && !getTelegramInitData()) invalidateBrowserSession();
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, body);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(buildApiUrl(path), {
    method: 'GET',
    headers: buildHeaders(),
    signal,
  });
  return handleResponse<T>(res);
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(buildApiUrl(path), {
    method: 'POST',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(buildApiUrl(path), {
    method: 'PUT',
    headers: buildHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(buildApiUrl(path), {
    method: 'DELETE',
    headers: buildHeaders(),
  });
  return handleResponse<T>(res);
}

export async function apiGetWithEtag<T>(
  path: string,
  etag?: string,
): Promise<{ data: T | null; etag: string; notModified: boolean }> {
  const headers = buildHeaders();
  if (etag) {
    headers['If-None-Match'] = etag;
  }

  const res = await fetch(buildApiUrl(path), {
    method: 'GET',
    headers,
  });

  const responseEtag = res.headers.get('ETag') ?? etag ?? '';
  if (res.status === 304) {
    return { data: null, etag: responseEtag, notModified: true };
  }
  if (!res.ok) {
    if (res.status === 401 && !getTelegramInitData()) invalidateBrowserSession();
    const body = await res.text().catch(() => '');
    throw new ApiError(res.status, res.statusText, body);
  }

  return {
    data: (await res.json()) as T,
    etag: responseEtag,
    notModified: false,
  };
}
